import { EstateAssets, DecisionResult } from '../../types';

export interface ProbateResolution {
    status: 'REQUIRED' | 'UNLIKELY' | 'UNCLEAR';
    statusLabel: string;
    shortReason: string;
    resolveActions: Array<{
        label: string;
        route: string; // 'property' | 'banks' | 'investments' | 'review'
        focus?: string; // specific element ID or section
    }>;
}

// Helper to check if ownership is strictly known
const isOwnershipKnown = (type: string | null | undefined) => {
    return type && type !== 'unknown';
};

// Helper to check valid share
const isShareKnown = (percent: number | null | undefined) => {
    return typeof percent === 'number' && percent >= 0;
};

export interface ProbateLogicParams {
    assets: EstateAssets;
    decisions?: Record<string, DecisionResult>;
    pendingItems?: string[];
}

export const getProbateStatusExplanation = ({ assets, decisions, pendingItems }: ProbateLogicParams): ProbateResolution => {

    // Default / Fallback
    const result: ProbateResolution = {
        status: 'UNCLEAR',
        statusLabel: "Probate unclear (needs confirmation)",
        shortReason: "We don't yet have enough detail to confirm whether probate is needed.",
        resolveActions: []
    };

    // ---------------------------------------------------------
    // Pre-compute: does ANY known asset already require probate?
    // ---------------------------------------------------------
    const propertyRequiresProbate = assets.properties.some(p => {
        if (p.ownership_type === 'sole') return true;
        if (p.ownership_type === 'tenants_in_common') return true;
        return false;
    });

    const soleFinancialRequiresProbate =
        assets.bank_accounts.some(b => b.ownership === 'sole') ||
        assets.investments.some(i => i.ownership === 'sole');

    const anyAssetRequiresProbate = propertyRequiresProbate || soleFinancialRequiresProbate;

    // ---------------------------------------------------------
    // Pre-compute: is any ownership info missing?
    // ---------------------------------------------------------
    const missingPropertyInfo = assets.properties.some(p =>
        !isOwnershipKnown(p.ownership_type) ||
        (p.ownership_type !== 'sole' && !isShareKnown(p.deceased_share_percent))
    );

    const missingBankInfo = assets.bank_accounts.some(b => !isOwnershipKnown(b.ownership));
    const missingInvInfo = assets.investments.some(i => !isOwnershipKnown(i.ownership));
    const hasMissingInfo = missingPropertyInfo || missingBankInfo || missingInvInfo;

    // ---------------------------------------------------------
    // RULE A: If any known asset requires probate, status = REQUIRED
    // even if other assets still have missing ownership info.
    // ---------------------------------------------------------
    if (anyAssetRequiresProbate) {
        const hasTic = assets.properties.some(p => p.ownership_type === 'tenants_in_common');
        result.status = 'REQUIRED';
        result.statusLabel = "Probate likely required";
        result.shortReason = "At least one asset in the estate appears to require probate. Based on the information entered, a grant of probate will likely be needed to deal with the deceased's share.";
        result.resolveActions.push({
            label: "Review property details",
            route: "property"
        });

        // If there's also missing info, add resolve actions for those
        if (hasMissingInfo) {
            if (missingPropertyInfo) {
                result.resolveActions.push({
                    label: "Review ownership details",
                    route: "property",
                    focus: "ownership_type"
                });
            }
            if (missingBankInfo) {
                result.resolveActions.push({
                    label: "Review bank accounts",
                    route: "banks",
                    focus: "ownership"
                });
            }
            if (missingInvInfo) {
                result.resolveActions.push({
                    label: "Review investments",
                    route: "investments",
                    focus: "ownership"
                });
            }
        }
        return result;
    }

    // ---------------------------------------------------------
    // RULE B: Missing Ownership Info => UNCLEAR
    // (only reached if no known asset already requires probate)
    // ---------------------------------------------------------
    if (hasMissingInfo) {
        result.status = 'UNCLEAR';
        result.statusLabel = "Probate unclear (needs confirmation)";
        result.shortReason = "We don't yet have enough ownership detail to confirm whether probate is needed.";

        // Context-sensitive CTA
        if (missingPropertyInfo) {
            result.resolveActions.push({
                label: "Review ownership details",
                route: "property",
                focus: "ownership_type"
            });
        }
        if (missingBankInfo) {
            result.resolveActions.push({
                label: "Review bank accounts",
                route: "banks",
                focus: "ownership"
            });
        }
        if (missingInvInfo) {
            result.resolveActions.push({
                label: "Review investments",
                route: "investments",
                focus: "ownership"
            });
        }
        return result;
    }

    // ---------------------------------------------------------
    // RULE C: High/Sole Financials => UNCLEAR
    // (already handled above if sole — this is now unreachable
    //  for sole, but kept for completeness)
    // ---------------------------------------------------------

    // ---------------------------------------------------------
    // RULE D: All assets pass automatically => UNLIKELY
    // ---------------------------------------------------------

    result.status = 'UNLIKELY';
    result.statusLabel = "Probate unlikely";
    result.shortReason = "Most assets appear to pass automatically without a grant (e.g. joint assets), though providers can still vary.";
    result.resolveActions.push({
        label: "Double check with banks",
        route: "banks"
    });

    return result;
};
